/**
 * @fileoverview A página do convite da equipe (etapa 5 dos planos), aberta pelo
 * link do e-mail (/invite#code=...): quem convidou, a Empresa, as contas e o
 * perfil. Sem conta, a pessoa cria a senha aqui e já entra; com conta, entra
 * pelo login de sempre e volta para aceitar; logada com o mesmo e-mail, aceita
 * (quem ainda não tinha conta ganha a do convite e a sessão vira a de sempre).
 * Logada com outro e-mail, sai e entra com o do convite. Abre logado ou não,
 * fora da esteira e do app.
 */

import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { Eye, EyeOff } from "lucide-react";

import AuthLayout from "../../components/AuthLayout";
import MfaSetupDialog from "../../components/MfaSetupDialog";
import { useAuthFormStyles } from "../../components/AuthLayout/formStyles";
import TermsCheckbox from "../../components/AuthLayout/TermsCheckbox";
import { errorCode, errorText, takeLinkCode } from "../../components/AuthLayout/authHelpers";
import { AuthContext } from "../../context/Auth/AuthContext";
import useLanguage from "../../hooks/useLanguage";
import api, { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";
import { formatDate } from "../../utils/formatLocaleDate";
import { clearPendingInvite, keepPendingInvite, markInviteReturn, readPendingInvite } from "../../utils/pendingInvite";

const t = (key, options) => i18n.t(`invitePage.${key}`, options);
const FIELD_OF_ERROR = { ERR_TERMS_NOT_ACCEPTED: "terms", ERR_SIGNUP_INVALID_NAME: "name", ERR_PASSWORD_TOO_SHORT: "password", ERR_PASSWORD_TOO_LONG: "password" };

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  return {
    details: { margin: "0 0 24px", padding: "2px 16px", border: `1px solid ${line}`, borderRadius: 12 },
    detail: {
      display: "grid", gridTemplateColumns: "88px minmax(0, 1fr)", gap: 12, padding: "10px 0", fontSize: 14, lineHeight: 1.45,
      "& + &": { borderTop: `1px solid ${line}` },
    },
    detailLabel: { margin: 0, color: dark ? "#A1A1AA" : "#52525B" },
    detailValue: { margin: 0, color: dark ? "#FAFAFA" : "#09090B", fontWeight: 500, overflowWrap: "anywhere" },
    actions: { display: "flex", flexDirection: "column", gap: 10 },
  };
});

// O código do link (que sai do endereço) ou o guardado na aba antes do login.
const openingCode = () => {
  const code = takeLinkCode() || readPendingInvite();
  if (code) keepPendingInvite(code);
  return code;
};

// A sessão da conta do convite no lugar da de antes: o app recarrega nela.
const enterAccount = (data) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("companyId", String(data.user.companyId));
  localStorage.setItem("userId", String(data.user.id));
  window.location.assign("/");
};

const Invite = () => {
  useLanguage(); // trocou o idioma no globo: redesenha já traduzida
  const form = useAuthFormStyles();
  const classes = useStyles();
  const history = useHistory();
  const { isAuth, user, loading, startSession, handleLogout, switchCompany } = useContext(AuthContext);
  const [code] = useState(openingCode);
  const [view, setView] = useState(code ? "loading" : "invalid");
  const [invite, setInvite] = useState(null);
  const [fields, setFields] = useState({ name: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [joinedId, setJoinedId] = useState(null);
  const [setupTicket, setSetupTicket] = useState("");
  // O aceite que não muda tentando de novo (o perfil de outra Empresa, outro e-mail).
  const [stuck, setStuck] = useState(false);

  const invalid = () => {
    clearPendingInvite();
    setView("invalid");
  };

  const load = () => {
    setView("loading");
    openApi
      .post("/invites/preview", { code })
      .then(({ data }) => {
        setInvite(data);
        setView("ready");
      })
      .catch((err) => (errorCode(err) === "ERR_INVITE_INVALID" ? invalid() : setView("failed")));
  };

  useEffect(() => {
    if (code) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outro link de convite aberto nesta mesma aba (só o # muda, a página não recarrega): recarrega com ele.
  useEffect(() => {
    const onHash = () => /code=[0-9a-f]{64}/.test(window.location.hash) && window.location.reload();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const signedIn = isAuth && !!user?.email;
  const sameEmail = signedIn && !!invite && user.email.trim().toLowerCase() === invite.email;
  const joinedName = invite && joinedId ? (invite.accounts.find((account) => account.id === joinedId) || invite.accounts[0])?.name : "";

  const change = (event) => {
    setFields({ ...fields, [event.target.name]: event.target.value });
    if (fieldErrors[event.target.name]) setFieldErrors({ ...fieldErrors, [event.target.name]: "" });
  };

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/invites/accept", { code });
      clearPendingInvite();
      // Quem ainda não tinha conta ganha a do convite: a sessão vira a de sempre.
      if (data.token) {
        enterAccount(data);
        return;
      }
      setJoinedId(data.companyId);
      setView("joined");
    } catch (err) {
      const reason = errorCode(err);
      if (reason === "ERR_INVITE_INVALID") invalid();
      else {
        if (reason === "ERR_INVITE_PROFILE_MISMATCH" || reason === "ERR_INVITE_OTHER_EMAIL") {
          clearPendingInvite();
          setStuck(true);
        }
        setError(errorText(err));
      }
    }
    setBusy(false);
  };

  const signup = async (event) => {
    event.preventDefault();
    if (busy) return;
    const name = fields.name.replace(/\s+/g, " ").trim();
    const next = {};
    if (name.length < 2) next.name = i18n.t("auth.signup.errors.name");
    if (fields.password.length < 8) next.password = i18n.t("backendErrors.ERR_PASSWORD_TOO_SHORT");
    if (!acceptedTerms) next.terms = i18n.t("backendErrors.ERR_TERMS_NOT_ACCEPTED");
    setFieldErrors(next);
    if (next.name || next.password || next.terms) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await openApi.post("/invites/signup", { code, name, password: fields.password, acceptedTerms });
      clearPendingInvite();
      if (data.mfaSetupRequired) {
        // A conta pede a verificação em duas etapas desde o começo: configura e entra.
        setSetupTicket(data.setupTicket);
        setBusy(false);
        return;
      }
      startSession(data);
    } catch (err) {
      const reason = errorCode(err);
      setBusy(false);
      if (reason === "ERR_INVITE_INVALID") {
        invalid();
        return;
      }
      // O e-mail ganhou uma conta no meio do caminho: entra com ela para aceitar.
      if (reason === "ERR_INVITE_LOGIN_REQUIRED") setInvite({ ...invite, hasAccount: true });
      if (FIELD_OF_ERROR[reason]) setFieldErrors({ [FIELD_OF_ERROR[reason]]: errorText(err) });
      else setError(errorText(err));
    }
  };

  const goToLogin = () => {
    keepPendingInvite(code);
    markInviteReturn();
    history.push("/login");
  };

  // Logada e sem querer o convite agora: ele sai da aba e a pessoa segue no sistema.
  const notNow = () => {
    clearPendingInvite();
    window.location.assign("/");
  };

  // Logada com outro e-mail: sai (o que apaga o convite da aba), guarda o convite de
  // novo e volta para ele, sem a sessão.
  const switchUser = async () => {
    await handleLogout();
    keepPendingInvite(code);
    history.push("/invite");
  };

  const goToJoined = async () => {
    setBusy(true);
    try {
      await switchCompany(joinedId);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const goOn = () => (isAuth ? window.location.assign("/") : history.push("/login"));

  const details = invite && (
    <dl className={classes.details}>
      <div className={classes.detail}>
        <dt className={classes.detailLabel}>{t("company")}</dt>
        <dd className={classes.detailValue}>{invite.companyName}</dd>
      </div>
      <div className={classes.detail}>
        <dt className={classes.detailLabel}>{t(invite.accounts.length > 1 ? "accounts" : "account")}</dt>
        <dd className={classes.detailValue}>{invite.accounts.map((account) => account.name).join(", ")}</dd>
      </div>
      <div className={classes.detail}>
        <dt className={classes.detailLabel}>{t("profile")}</dt>
        <dd className={classes.detailValue}>{t(`profiles.${invite.profile === "admin" ? "admin" : "user"}`)}</dd>
      </div>
      <div className={classes.detail}>
        <dt className={classes.detailLabel}>{t("email")}</dt>
        <dd className={classes.detailValue}>{invite.email}</dd>
      </div>
      <div className={classes.detail}>
        <dt className={classes.detailLabel}>{t("expires")}</dt>
        <dd className={classes.detailValue}>{formatDate(invite.expiresAt)}</dd>
      </div>
    </dl>
  );

  const errorNotice = error && <div className={`${form.notice} ${form.noticeBad}`} role="alert">{error}</div>;

  const readyBody = () => {
    if (sameEmail) {
      return (
        <>
          <p className={form.subtitle}>{t("acceptSubtitle")}</p>
          {details}
          {errorNotice}
          <div className={classes.actions}>
            {!stuck && (
              <button type="button" className={form.button} onClick={accept} disabled={busy}>
                {busy && <CircularProgress size={18} className={form.spinner} />}
                {t("accept")}
              </button>
            )}
            <button type="button" className={form.secondary} onClick={notNow} disabled={busy}>
              {stuck ? i18n.t("auth.verify.goToApp") : t("notNow")}
            </button>
          </div>
        </>
      );
    }
    if (signedIn) {
      return (
        <>
          <p className={form.subtitle}>{t("otherEmail", { current: user.email, email: invite.email })}</p>
          {details}
          <div className={classes.actions}>
            <button type="button" className={form.button} onClick={switchUser}>{t("switchUser")}</button>
            <button type="button" className={form.secondary} onClick={notNow}>{t("notNow")}</button>
          </div>
        </>
      );
    }
    if (invite.hasAccount) {
      return (
        <>
          <p className={form.subtitle}>{t("hasAccount")}</p>
          {details}
          {errorNotice}
          <button type="button" className={form.button} onClick={goToLogin}>{t("login")}</button>
        </>
      );
    }
    return (
      <form noValidate onSubmit={signup}>
        <p className={form.subtitle}>{t("createSubtitle")}</p>
        {details}
        <div className={form.field}>
          <label className={form.label} htmlFor="invite-name">{t("name")}</label>
          <input
            id="invite-name"
            name="name"
            className={form.input}
            value={fields.name}
            onChange={change}
            autoComplete="name"
            maxLength={120}
            aria-invalid={Boolean(fieldErrors.name)}
            aria-describedby={fieldErrors.name ? "invite-name-error" : undefined}
          />
          {fieldErrors.name && <div id="invite-name-error" className={form.error}>{fieldErrors.name}</div>}
        </div>
        <div className={form.field}>
          <label className={form.label} htmlFor="invite-password">{t("password")}</label>
          <div className={form.inputWrap}>
            <input
              id="invite-password"
              name="password"
              type={showPassword ? "text" : "password"}
              className={`${form.input} ${form.inputWithButton}`}
              value={fields.password}
              onChange={change}
              autoComplete="new-password"
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? "invite-password-error" : "invite-password-hint"}
            />
            <button
              type="button"
              className={form.eye}
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? i18n.t("auth.hidePassword") : i18n.t("auth.showPassword")}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {fieldErrors.password ? (
            <div id="invite-password-error" className={form.error}>{fieldErrors.password}</div>
          ) : (
            <div id="invite-password-hint" className={form.hint}>{t("passwordHint")}</div>
          )}
        </div>
        <TermsCheckbox
          id="invite-terms"
          checked={acceptedTerms}
          onChange={(value) => {
            setAcceptedTerms(value);
            if (fieldErrors.terms) setFieldErrors({ ...fieldErrors, terms: "" });
          }}
          error={fieldErrors.terms}
        />
        {errorNotice}
        <button type="submit" className={form.button} disabled={busy}>
          {busy && <CircularProgress size={18} className={form.spinner} />}
          {t("create")}
        </button>
      </form>
    );
  };

  return (
    <AuthLayout>
      {(view === "loading" || (view === "ready" && loading)) && (
        <div className={form.view} role="status" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CircularProgress size={20} />
          <span>{t("loading")}</span>
        </div>
      )}
      {view === "ready" && !loading && (
        <div className={form.view}>
          <h1 className={form.title}>{invite.inviterName ? t("title", { name: invite.inviterName }) : t("titleNoInviter")}</h1>
          {readyBody()}
        </div>
      )}
      {view === "joined" && (
        <div className={form.view}>
          <h1 className={form.title}>{t("joinedTitle")}</h1>
          <p className={form.subtitle}>{t("joined", { account: joinedName })}</p>
          {errorNotice}
          <div className={classes.actions}>
            <button type="button" className={form.button} onClick={goToJoined} disabled={busy}>
              {busy && <CircularProgress size={18} className={form.spinner} />}
              {t("goToAccount", { account: joinedName })}
            </button>
            <button type="button" className={form.secondary} onClick={() => window.location.assign("/")}>{t("stay")}</button>
          </div>
        </div>
      )}
      {view === "failed" && (
        <div className={form.view}>
          <h1 className={form.title}>{t("failedTitle")}</h1>
          <p className={form.subtitle}>{i18n.t("auth.errors.generic")}</p>
          <button type="button" className={form.button} onClick={load}>{t("retry")}</button>
        </div>
      )}
      {view === "invalid" && (
        <div className={form.view}>
          <h1 className={form.title}>{t("invalidTitle")}</h1>
          <p className={form.subtitle}>{t("invalid")}</p>
          <button type="button" className={form.button} onClick={goOn}>{isAuth ? i18n.t("auth.verify.goToApp") : i18n.t("auth.backToLogin")}</button>
        </div>
      )}

      <MfaSetupDialog open={!!setupTicket} setupTicket={setupTicket} forceMode onClose={() => setSetupTicket("")} />
    </AuthLayout>
  );
};

export default Invite;
